import React, { forwardRef, SyntheticEvent, useMemo, useState } from 'react';
import UserAvatar from '@components/UserAvatar';
import { Avatar, Button, Select } from 'antd';
import type { BaseSelectRef } from 'rc-select/lib/BaseSelect';
import { AWPotentialReviewerDto } from '@api/Api';
import { useDebounced } from '@helpers/useDebounced';
import { nonNull } from '@helpers/non-null';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { BaseOptionType } from 'antd/lib/select';
import { hashedColor } from '@helpers/hashedColor';
import { uuidValidatorRegExp, extractValidEmails } from '@helpers/validators';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  getPotentialsApprovalReviewerGroups,
  getPotentialsApprovalReviewers
} from '@api/Approval';
import Icon from '@ant-design/icons';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';

import './ReviewersSelect.less';

interface ReviewersSelectProps {
  campaignId: string;
  limitReached?: boolean;
  stageFinished?: boolean;
  excludeEmails?: string[];
  onAddReviewers?: (
    values: { email: string; reviewer?: AWPotentialReviewerDto }[]
  ) => void;
  onRemoveReviewer?: (value: AWPotentialReviewerDto) => void;
  queryEnabled?: boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

export default forwardRef(function ReviewersSelect(
  {
    campaignId,
    limitReached,
    stageFinished,
    excludeEmails,
    onAddReviewers,
    onRemoveReviewer,
    queryEnabled,
    getPopupContainer
  }: ReviewersSelectProps,
  forwardedRef: React.Ref<BaseSelectRef>
) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const memberGroupsQuery = useInfiniteQuery({
    queryKey: [
      'approval',
      'potentialReviewerGroups',
      campaignId,
      {
        exclude: excludeEmails,
        searchQuery: debouncedSearchQuery
      }
    ],
    keepPreviousData: true,
    enabled: queryEnabled,
    queryFn: async (ctx) => {
      const { data } = await getPotentialsApprovalReviewerGroups(
        {
          campaignId: nonNull(campaignId),
          exclude: excludeEmails,
          after: ctx.pageParam,
          searchQuery
        },
        { signal: ctx.signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
  const potentialReviewersQuery = useInfiniteQuery({
    queryKey: [
      'approval',
      'potentialReviewers',
      campaignId,
      {
        exclude: excludeEmails,
        searchQuery: debouncedSearchQuery
      }
    ],
    keepPreviousData: true,
    enabled: queryEnabled,
    queryFn: async (ctx) => {
      const { data } = await getPotentialsApprovalReviewers(
        {
          campaignId: nonNull(campaignId),
          exclude: excludeEmails,
          after: ctx.pageParam,
          searchQuery: debouncedSearchQuery
        },
        { signal: ctx.signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
  const potentialReviewersList = useMemo(
    () =>
      (
        potentialReviewersQuery.data?.pages.flatMap((x) =>
          x.edges.map((x) => x.node)
        ) ?? []
      ).filter((x) => !excludeEmails?.includes(x.email)),
    [potentialReviewersQuery.data, excludeEmails]
  );
  const memberGroupsList = useMemo(
    () =>
      memberGroupsQuery.data?.pages.flatMap((x) =>
        x.edges.map((x) => x.node)
      ) ?? [],
    [memberGroupsQuery.data]
  );

  const memberGroupsLoading =
    memberGroupsQuery.isLoading ||
    memberGroupsQuery.isFetchingNextPage ||
    memberGroupsQuery.isPreviousData;
  const potentialReviewersLoading =
    potentialReviewersQuery.isLoading ||
    potentialReviewersQuery.isFetchingNextPage ||
    potentialReviewersQuery.isPreviousData;

  const loading = memberGroupsLoading || potentialReviewersLoading;
  const showPotentialReviewers =
    memberGroupsQuery.hasNextPage === false || memberGroupsQuery.isPreviousData;

  const [groupLoading, setGroupLoading] = useState(0);

  const handleSelect = async (value: string) => {
    setSearchQuery('');
    setDropdownVisible(false);
    if (!onAddReviewers) return;
    value = value.trim().toLowerCase();
    const isId = uuidValidatorRegExp.test(value);
    if (!isId) return;
    const group = memberGroupsList.find((x) => x.id === value);
    if (!group) return;
    setGroupLoading((count) => count + 1);
    try {
      const {
        data: { edges }
      } = await getPotentialsApprovalReviewers({
        campaignId,
        groupId: group.id,
        exclude: excludeEmails,
        limit: 100
      });
      onAddReviewers(
        edges.map((x) => ({ email: x.node.email, reviewer: x.node }))
      );
    } finally {
      setGroupLoading((count) => Math.max(0, count - 1));
    }
  };

  const handleChange = async (values: string[]) => {
    setSearchQuery('');
    setDropdownVisible(false);
    if (!onAddReviewers) return;
    const emails = extractValidEmails(values);
    if (emails.length === 0) return;
    const reviewers = emails.map((email) => {
      const reviewer = potentialReviewersList.find(
        (x) => x.email.toLowerCase() === email.toLowerCase()
      );
      return { email, reviewer };
    });
    onAddReviewers(reviewers);
  };

  const loadMoreData = (e: SyntheticEvent) => {
    const query = showPotentialReviewers
      ? potentialReviewersQuery
      : memberGroupsQuery;
    if (query.isFetchingNextPage || !query.hasNextPage) return;
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      query.fetchNextPage();
    }
  };

  const groupOptions = useMemo<BaseOptionType[]>(() => {
    return memberGroupsList.map((item) => ({
      value: item.id,
      label: (
        <div className="list_item">
          <div className="list_item__avatar">
            <Avatar
              size={40}
              shape="square"
              style={{
                background: hashedColor(item.id, 'memberGroupAvatar')
              }}
            >
              {item.name[0].toUpperCase()}
            </Avatar>
          </div>
          <div className="list_item__user_info">
            <div className="list_item__user_info__name">{item.name}</div>
            <div className="list_item__user_info__email">
              {item.membersCount} members
            </div>
          </div>
        </div>
      )
    }));
  }, [memberGroupsList]);
  const potentialReviewersOptions = useMemo<BaseOptionType[]>(() => {
    return potentialReviewersList.map((item) => {
      return {
        value: item.email,
        label: (
          <div className="list_item">
            <div className="list_item__avatar">
              <UserAvatar
                isActive
                size="large"
                src={item.picture.url}
                userEmail={item.email}
                userName={item.name || ''}
              />
            </div>
            <div className="list_item__user_info">
              <div className="list_item__user_info__name">{item.name}</div>
              <div className="list_item__user_info__email">{item.email}</div>
            </div>
          </div>
        )
      };
    });
  }, [potentialReviewersList]);

  const options = useMemo(() => {
    return showPotentialReviewers
      ? [...groupOptions, ...potentialReviewersOptions]
      : groupOptions;
  }, [groupOptions, potentialReviewersOptions, showPotentialReviewers]);

  const open = dropdownVisible && options.length > 0;
  const disabled = limitReached || stageFinished;

  return (
    <div className="approval-reviewers-select">
      <div className="approval-reviewers-select__container">
        <UpgradePlanTooltip
          text="your plan to add more reviewers."
          isCanVisible={!!limitReached}
          placement="topLeft"
        >
          <Select
            ref={forwardedRef}
            getPopupContainer={getPopupContainer}
            disabled={disabled}
            mode="tags"
            size="large"
            listHeight={280}
            listItemHeight={64}
            placeholder="Invite by email or from list"
            tokenSeparators={[',', ' ', '\r', '\n']}
            showArrow={false}
            filterOption={false}
            autoClearSearchValue={false}
            searchValue={searchQuery}
            onSearch={(value) => setSearchQuery(value)}
            onChange={(values) => handleChange(values)}
            onSelect={(value) => handleSelect(value)}
            onDeselect={(value) => {
              if (!value) return;
              if (!onRemoveReviewer) return;
              const member = JSON.parse(value);
              onRemoveReviewer(member);
            }}
            popupClassName="approval-reviewers-select__list"
            onPopupScroll={loadMoreData}
            open={open}
            onDropdownVisibleChange={setDropdownVisible}
            loading={loading}
            value={[]}
            options={options}
          />
        </UpgradePlanTooltip>

        <UpgradePlanTooltip
          text="your plan to add more reviewers."
          isCanVisible={!!limitReached}
        >
          <Button
            type="ghost"
            icon={
              limitReached ? <Icon component={UpgradePlanSvg} /> : <PlusSvg />
            }
            className="invite-reviewers_btn"
            disabled={limitReached || !searchQuery}
            loading={groupLoading > 0}
          >
            Add
          </Button>
        </UpgradePlanTooltip>
      </div>
    </div>
  );
});
